import { useEffect, useRef } from "react";
import type { Deck } from "./engine";

/**
 * A platter. Dragging it round while the deck plays nudges the speed in
 * proportion to how fast it is turned, and letting go lets the fader have
 * the deck back; dragging it while paused scrubs, a turn being one
 * revolution of a 33 -- 1.8 seconds. It also turns by itself as the track
 * plays, which is how you can see a deck is running from across the room.
 */

/** Seconds of track per revolution when scrubbing: a 33⅓ rpm record. */
const SECONDS_PER_TURN = 1.8;
/** A turn a second while playing is this much faster. */
const BEND_PER_TURN_PER_SECOND = 0.08;
/** How long a nudge outlives the last movement of the hand. */
const BEND_HOLD_MS = 160;

export function JogWheel({ deck }: { deck: Deck }) {
  const el = useRef<HTMLDivElement>(null);
  const drag = useRef<{ angle: number; at: number; turned: number } | null>(null);

  // Spun by the track's own time, so the wheel reads as a platter and not a
  // dial -- and when a drag scrubs, the time moves and the wheel with it.
  useEffect(() => {
    let frame = 0;
    const spin = () => {
      frame = requestAnimationFrame(spin);
      const w = el.current;
      if (!w) return;
      const deg = ((deck.time() / SECONDS_PER_TURN) * 360) % 360;
      w.style.setProperty("--spin", `${deg}deg`);
      w.classList.toggle("running", deck.playing);
    };
    spin();
    return () => cancelAnimationFrame(frame);
  }, [deck]);

  const angleOf = (e: React.PointerEvent) => {
    const r = e.currentTarget.getBoundingClientRect();
    return Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2));
  };

  return (
    <div ref={el} className="jog" role="slider" aria-label="jog wheel" tabIndex={-1}
         onPointerDown={(e) => {
           if (!deck.loaded) return;
           e.currentTarget.setPointerCapture(e.pointerId);
           drag.current = { angle: angleOf(e), at: e.timeStamp, turned: 0 };
         }}
         onPointerMove={(e) => {
           const d = drag.current;
           if (!d) return;
           const a = angleOf(e);
           let delta = a - d.angle;
           // The shorter way round the circle: a hand does not jump 350°.
           if (delta > Math.PI) delta -= 2 * Math.PI;
           if (delta < -Math.PI) delta += 2 * Math.PI;
           const dt = Math.max(1, e.timeStamp - d.at) / 1000;
           d.angle = a;
           d.at = e.timeStamp;
           const turns = delta / (2 * Math.PI);
           if (deck.playing) {
             const bend = Math.max(-0.5, Math.min(0.5, (turns / dt) * BEND_PER_TURN_PER_SECOND));
             deck.bend(1 + bend, BEND_HOLD_MS);
           } else {
             deck.seek(deck.time() + turns * SECONDS_PER_TURN);
           }
         }}
         onPointerUp={(e) => {
           if (drag.current) e.currentTarget.releasePointerCapture(e.pointerId);
           drag.current = null;
           // Back to the fader's speed at once rather than after the hold.
           deck.bend(1, 0);
         }}
         onPointerCancel={() => { drag.current = null; deck.bend(1, 0); }}>
      <div className="platter"><span className="mark" /></div>
    </div>
  );
}
